import type { StateCreator } from 'zustand'
import type { AppState, PaymentState, PaymentResult, TipOption } from '../types'

export type PaymentSlice = Pick<
  AppState,
  | 'payment'
  | 'setTipOption'
  | 'setCustomTipAmount'
  | 'setPaymentLoading'
  | 'setPaymentSuccess'
  | 'setPaymentError'
  | 'getTipAmount'
  | 'getFinalTotal'
  | 'resetPayment'
>

const initialState: PaymentState = {
  tipOption: 18, // Default to 18% - will be overridden by middle suggestion
  customTipAmount: 0,
  paymentResult: { status: 'idle' },
}

export const createPaymentSlice: StateCreator<
  AppState,
  [['zustand/devtools', never], ['zustand/persist', unknown]],
  [],
  PaymentSlice
> = (set, get) => ({
  payment: initialState,

  setTipOption: (option: TipOption) =>
    set(
      (state) => ({ payment: { ...state.payment, tipOption: option } }),
      undefined,
      'payment/setTipOption'
    ),

  setCustomTipAmount: (amount) =>
    set(
      (state) => ({ payment: { ...state.payment, customTipAmount: amount } }),
      undefined,
      'payment/setCustomTipAmount'
    ),

  setPaymentLoading: () =>
    set(
      (state) => ({
        payment: { ...state.payment, paymentResult: { status: 'loading' } },
      }),
      undefined,
      'payment/setPaymentLoading'
    ),

  setPaymentSuccess: (result: PaymentResult) =>
    set(
      (state) => ({
        payment: {
          ...state.payment,
          paymentResult: { status: 'success', data: result },
        },
      }),
      undefined,
      'payment/setPaymentSuccess'
    ),

  setPaymentError: (error) =>
    set(
      (state) => ({
        payment: { ...state.payment, paymentResult: { status: 'error', error } },
      }),
      undefined,
      'payment/setPaymentError'
    ),

  getTipAmount: () => {
    const { tipOption, customTipAmount } = get().payment
    const subtotal = get().getSubtotal()

    if (tipOption === 'none') return 0
    if (tipOption === 'custom') return customTipAmount
    // tipOption is a percentage number (e.g., 15, 18, 20)
    return subtotal * (tipOption / 100)
  },

  getFinalTotal: () => {
    const total = get().getTotal()
    const tipAmount = get().getTipAmount()
    return total + tipAmount
  },

  resetPayment: () =>
    set({ payment: initialState }, undefined, 'payment/reset'),
})
